import config from 'src/config';
import Permissions from 'src/security/permissions';

const permissions = Permissions.values;

const privateRoutes = [
  {
    path: '/',
    loader: () => import('src/view/dashboard/DashboardPage'),
    permissionRequired: null,
    exact: true,
  },
  {
    path: '/admin-panel',
    loader: () => import('src/admin-panel/AdminPanelPage'),
    permissionRequired: permissions.superAdmin,
    exact: true,
    hasSubroutes: true,
    subRoutes: [
      {
        path: '/admin-panel/users',
        loader: () => import('src/admin-panel/pages/AdminUsers'),
        permissionRequired: permissions.superAdmin,
      },
      {
        path: '/admin-panel/user/:id',
        loader: () => import('src/admin-panel/pages/AdminUsertDetails'),
        permissionRequired: permissions.superAdmin,
        exact: true,
      },

      {
        path: '/admin-panel/user/:id/tenant/:tenantId',
        loader: () => import('src/admin-panel/pages/AdminTenantEdit'),
        permissionRequired: permissions.superAdmin,
        exact: true,
      },
      {
        path: '/admin-panel/user/:id/tenant/:tenantId/e-labels',
        loader: () => import('src/admin-panel/pages/AdminTenantElabels'),
        permissionRequired: permissions.superAdmin,
        exact: true,
      },
      {
        path: '/admin-panel/user/:id/tenant/:tenantId/e-label/:elabelId/edit',
        loader: () => import('src/admin-panel/pages/AdminTenantElabelEdit'),
        permissionRequired: permissions.superAdmin,
        exact: true,
      },
      {
        path: '/admin-panel/default-images',
        loader: () => import('src/admin-panel/pages/DefaultImages'),
        permissionRequired: permissions.superAdmin,
      },
      {
        path: '/admin-panel/create-contract',
        loader: () => import('src/admin-panel/pages/CreateContract'),
        permissionRequired: permissions.superAdmin,
      },
      {
        path: '/admin-panel/upload-contacts/plunk',
        loader: () => import('src/admin-panel/pages/UploadContactsPlunk'),
        permissionRequired: permissions.superAdmin,
      },
      {
        path: '/admin-panel/notifications',
        loader: () => import('src/admin-panel/pages/ReleaseNotifications'),
        permissionRequired: permissions.superAdmin,
      },
    ],
  },

  {
    path: '/profile',
    loader: () => import('src/view/auth/ProfileFormPage'),
    permissionRequired: null,
    exact: true,
  },

  {
    path: '/tenant',
    loader: () => import('src/view/tenant/list/TenantListPage'),
    permissionRequired: null,
    exact: true,
  },
  {
    path: '/tenant/new',
    loader: () => import('src/view/tenant/form/TenantFormPage'),
    permissionRequired: null,
    exact: true,
  },
  {
    path: '/tenant/:id/edit',
    loader: () => import('src/view/tenant/form/TenantFormPage'),
    permissionRequired: null,
    exact: true,
  },

  config.isPlanEnabled && {
    path: '/plan',
    loader: () => import('src/view/plan/PlanPage'),
    permissionRequired: permissions.planRead,
    exact: true,
  },

  {
    path: '/user',
    loader: () => import('src/view/user/list/UserPage'),
    permissionRequired: permissions.userRead,
    exact: true,
  },

  {
    path: '/user/new',
    loader: () => import('src/view/user/new/UserNewPage'),
    permissionRequired: permissions.userCreate,
    exact: true,
  },

  {
    path: '/user/importer',
    loader: () => import('src/view/user/importer/UserImporterPage'),
    permissionRequired: permissions.userImport,
    exact: true,
  },
  {
    path: '/user/:id/edit',
    loader: () => import('src/view/user/edit/UserEditPage'),
    permissionRequired: permissions.userEdit,
    exact: true,
  },
  {
    path: '/user/:id',
    loader: () => import('src/view/user/view/UserViewPage'),
    permissionRequired: permissions.userRead,
    exact: true,
  },

  {
    path: '/history',
    loader: () => import('src/view/auditLog/AuditLogPage'),
    permissionRequired: permissions.auditLogRead,
  },

  {
    path: '/settings',
    loader: () => import('src/view/settings/SettingsFormPage'),
    permissionRequired: permissions.settingsEdit,
  },

  {
    path: '/e-label',
    loader: () => import('src/view/eLabel/list/ELabelListPage'),
    permissionRequired: permissions.eLabelRead,
    exact: true,
  },
  {
    path: '/e-label/new',
    loader: () => import('src/view/eLabel/form/ELabelFormPage'),
    permissionRequired: permissions.eLabelCreate,
    exact: true,
    checkPlanQuantity: true,
  },
  {
    path: '/e-label/importer',
    loader: () => import('src/view/eLabel/importer/ELabelImporterPage'),
    permissionRequired: permissions.eLabelImport,
    exact: true,
    checkPlanQuantity: true,
  },
  {
    path: '/e-label/:id/edit',
    loader: () => import('src/view/eLabel/form/ELabelFormPage'),
    permissionRequired: permissions.eLabelEdit,
    exact: true,
  },
  {
    path: '/e-label/:id',
    loader: () => import('src/view/eLabel/view/ELabelViewPage'),
    permissionRequired: permissions.eLabelRead,
    exact: true,
  },

  {
    path: '/analytics',
    loader: () => import('src/view/analytics/AnalyticsPage'),
    permissionRequired: permissions.analyticsRead,
    exact: true,
  },
  {
    path: '/analytics/:entityType/:id',
    loader: () => import('src/view/analytics/AnalyticsEntityPage'),
    permissionRequired: permissions.analyticsRead,
    exact: true,
  },

  {
    path: '/catalog',
    loader: () => import('src/view/catalog/list/CatalogListPage'),
    permissionRequired: permissions.catalogRead,
    exact: true,
  },
  {
    path: '/catalog/new',
    loader: () => import('src/view/catalog/form/CatalogFormPage'),
    permissionRequired: permissions.catalogCreate,
    exact: true,
  },
  {
    path: '/catalog/:id/edit',
    loader: () => import('src/view/catalog/form/CatalogFormPage'),
    permissionRequired: permissions.catalogEdit,
    exact: true,
  },
  {
    path: '/catalog/:id',
    loader: () => import('src/view/catalog/view/CatalogViewPage'),
    permissionRequired: permissions.catalogRead,
    exact: true,
  },

  {
    path: '/library',
    loader: () => import('src/view/library/LibraryPage'),
    permissionRequired: permissions.eLabelRead,
    exact: true,
    hasSubroutes: true,
    subRoutes: [
      {
        path: '/library/nutrition-information',
        loader: () =>
          import('src/view/nutritionInformation/list/NutritionInformationListPage'),
        permissionRequired: permissions.nutritionInformationRead,
        exact: true,
      },
      {
        path: '/library/nutrition-information/new',
        loader: () =>
          import('src/view/nutritionInformation/form/NutritionInformationFormPage'),
        permissionRequired: permissions.nutritionInformationCreate,
        exact: true,
      },
      {
        path: '/library/nutrition-information/importer',
        loader: () =>
          import('src/view/nutritionInformation/importer/NutritionInformationImporterPage'),
        permissionRequired: permissions.nutritionInformationImport,
        exact: true,
      },
      {
        path: '/library/nutrition-information/:id/edit',
        loader: () =>
          import('src/view/nutritionInformation/form/NutritionInformationFormPage'),
        permissionRequired: permissions.nutritionInformationEdit,
        exact: true,
      },
      {
        path: '/library/nutrition-information/:id',
        loader: () =>
          import('src/view/nutritionInformation/view/NutritionInformationViewPage'),
        permissionRequired: permissions.nutritionInformationRead,
        exact: true,
      },

      {
        path: '/library/ingredients',
        loader: () => import('src/view/ingredients/list/IngredientsListPage'),
        permissionRequired: permissions.nutritionInformationRead,
        exact: true,
      },
      {
        path: '/library/ingredients/new',
        loader: () => import('src/view/ingredients/form/IngredientsFormPage'),
        permissionRequired: permissions.nutritionInformationCreate,
        exact: true,
      },
      {
        path: '/library/ingredients/importer',
        loader: () =>
          import('src/view/ingredients/importer/IngredientsImporterPage'),
        permissionRequired: permissions.nutritionInformationImport,
        exact: true,
      },
      {
        path: '/library/ingredients/:id/edit',
        loader: () => import('src/view/ingredients/form/IngredientsFormPage'),
        permissionRequired: permissions.nutritionInformationEdit,
        exact: true,
      },
      {
        path: '/library/ingredients/:id',
        loader: () => import('src/view/ingredients/view/IngredientsViewPage'),
        permissionRequired: permissions.nutritionInformationRead,
        exact: true,
      },

      {
        path: '/library/image',
        loader: () => import('src/view/image/list/ImageListPage'),
        permissionRequired: permissions.imageRead,
        exact: true,
      },
      {
        path: '/library/image/new',
        loader: () => import('src/view/image/form/ImageFormPage'),
        permissionRequired: permissions.imageCreate,
        exact: true,
      },
      {
        path: '/library/image/importer',
        loader: () => import('src/view/image/importer/ImageImporterPage'),
        permissionRequired: permissions.imageImport,
        exact: true,
      },
      {
        path: '/library/image/:id/edit',
        loader: () => import('src/view/image/form/ImageFormPage'),
        permissionRequired: permissions.imageEdit,
        exact: true,
      },
      {
        path: '/library/image/:id',
        loader: () => import('src/view/image/view/ImageViewPage'),
        permissionRequired: permissions.imageRead,
        exact: true,
      },

      {
        path: '/library/sustainability',
        loader: () =>
          import('src/view/sustainability/list/SustainabilityListPage'),
        permissionRequired: permissions.sustainabilityRead,
        exact: true,
      },
      {
        path: '/library/sustainability/new',
        loader: () =>
          import('src/view/sustainability/form/SustainabilityFormPage'),
        permissionRequired: permissions.sustainabilityCreate,
        exact: true,
      },
      {
        path: '/library/sustainability/importer',
        loader: () =>
          import('src/view/sustainability/importer/SustainabilityImporterPage'),
        permissionRequired: permissions.sustainabilityImport,
        exact: true,
      },
      {
        path: '/library/sustainability/:id/edit',
        loader: () =>
          import('src/view/sustainability/form/SustainabilityFormPage'),
        permissionRequired: permissions.sustainabilityEdit,
        exact: true,
      },
      {
        path: '/library/sustainability/:id',
        loader: () =>
          import('src/view/sustainability/view/SustainabilityViewPage'),
        permissionRequired: permissions.sustainabilityRead,
        exact: true,
      },

      {
        path: '/library/packaging-certification',
        loader: () =>
          import('src/view/packagingCertification/list/PackagingCertificationListPage'),
        permissionRequired: permissions.packagingCertificationRead,
        exact: true,
      },
      {
        path: '/library/packaging-certification/new',
        loader: () =>
          import('src/view/packagingCertification/form/PackagingCertificationFormPage'),
        permissionRequired: permissions.packagingCertificationCreate,
        exact: true,
      },
      {
        path: '/library/packaging-certification/importer',
        loader: () =>
          import('src/view/packagingCertification/importer/PackagingCertificationImporterPage'),
        permissionRequired: permissions.packagingCertificationImport,
        exact: true,
      },
      {
        path: '/library/packaging-certification/:id/edit',
        loader: () =>
          import('src/view/packagingCertification/form/PackagingCertificationFormPage'),
        permissionRequired: permissions.packagingCertificationEdit,
        exact: true,
      },
      {
        path: '/library/packaging-certification/:id',
        loader: () =>
          import('src/view/packagingCertification/view/PackagingCertificationViewPage'),
        permissionRequired: permissions.packagingCertificationRead,
        exact: true,
      },

      {
        path: '/library/recycling-materials',
        loader: () =>
          import('src/view/recyclingMaterials/list/RecyclingMaterialsListPage'),
        permissionRequired: permissions.recyclingMaterialsRead,
        exact: true,
      },
      {
        path: '/library/recycling-materials/new',
        loader: () =>
          import('src/view/recyclingMaterials/form/RecyclingMaterialsFormPage'),
        permissionRequired: permissions.recyclingMaterialsCreate,
        exact: true,
      },
      {
        path: '/library/recycling-materials/importer',
        loader: () =>
          import('src/view/recyclingMaterials/importer/RecyclingMaterialsImporterPage'),
        permissionRequired: permissions.recyclingMaterialsImport,
        exact: true,
      },
      {
        path: '/library/recycling-materials/:id/edit',
        loader: () =>
          import('src/view/recyclingMaterials/form/RecyclingMaterialsFormPage'),
        permissionRequired: permissions.recyclingMaterialsEdit,
        exact: true,
      },
      {
        path: '/library/recycling-materials/:id',
        loader: () =>
          import('src/view/recyclingMaterials/view/RecyclingMaterialsViewPage'),
        permissionRequired: permissions.recyclingMaterialsRead,
        exact: true,
      },

      {
        path: '/library/responsible-consumption',
        loader: () =>
          import('src/view/responsibleConsumption/list/ResponsibleConsumptionListPage'),
        permissionRequired: permissions.responsibleConsumptionRead,
        exact: true,
      },
      {
        path: '/library/responsible-consumption/new',
        loader: () =>
          import('src/view/responsibleConsumption/form/ResponsibleConsumptionFormPage'),
        permissionRequired: permissions.responsibleConsumptionCreate,
        exact: true,
      },
      {
        path: '/library/responsible-consumption/importer',
        loader: () =>
          import('src/view/responsibleConsumption/importer/ResponsibleConsumptionImporterPage'),
        permissionRequired: permissions.responsibleConsumptionImport,
        exact: true,
      },
      {
        path: '/library/responsible-consumption/:id/edit',
        loader: () =>
          import('src/view/responsibleConsumption/form/ResponsibleConsumptionFormPage'),
        permissionRequired: permissions.responsibleConsumptionEdit,
        exact: true,
      },
      {
        path: '/library/responsible-consumption/:id',
        loader: () =>
          import('src/view/responsibleConsumption/view/ResponsibleConsumptionViewPage'),
        permissionRequired: permissions.responsibleConsumptionRead,
        exact: true,
      },

      {
        path: '/library/food-operator',
        loader: () => import('src/view/foodOperator/list/FoodOperatorListPage'),
        permissionRequired: permissions.foodOperatorRead,
        exact: true,
      },
      {
        path: '/library/food-operator/new',
        loader: () => import('src/view/foodOperator/form/FoodOperatorFormPage'),
        permissionRequired: permissions.foodOperatorCreate,
        exact: true,
      },
      {
        path: '/library/food-operator/importer',
        loader: () =>
          import('src/view/foodOperator/importer/FoodOperatorImporterPage'),
        permissionRequired: permissions.foodOperatorImport,
        exact: true,
      },
      {
        path: '/library/food-operator/:id/edit',
        loader: () => import('src/view/foodOperator/form/FoodOperatorFormPage'),
        permissionRequired: permissions.foodOperatorEdit,
        exact: true,
      },
      {
        path: '/library/food-operator/:id',
        loader: () => import('src/view/foodOperator/view/FoodOperatorViewPage'),
        permissionRequired: permissions.foodOperatorRead,
        exact: true,
      },
    ],
  },
  {
    path: '/tools',
    loader: () => import('src/view/tools/ToolsPage'),
    permissionRequired: permissions.eLabelRead,
    exact: true,
    hasSubroutes: true,
    subRoutes: [
      {
        path: '/tools/barcode',
        loader: () =>
          import('src/view/tools/barCodeGenerator/BarCodeGeneratorPage'),
        permissionRequired: permissions.nutritionInformationRead,
        exact: true,
      },
      {
        path: '/tools/qrcode',
        loader: () =>
          import('src/view/tools/qrCodeGenerator/QrCodeGeneratorPage'),
        permissionRequired: permissions.nutritionInformationCreate,
        exact: true,
      },
    ],
  },
].filter(Boolean);

const publicRoutes = [
  {
    path: '/auth/signin',
    loader: () => import('src/view/auth/SigninPage'),
  },
  {
    path: '/auth/signup',
    loader: () => import('src/view/auth/SignupPage'),
  },
  {
    path: '/auth/forgot-password',
    loader: () => import('src/view/auth/ForgotPasswordPage'),
  },
].filter(Boolean);

const emptyTenantRoutes = [
  {
    path: '/auth/tenant',
    loader: () => import('src/view/auth/TenantPage'),
  },
].filter(Boolean);

const emptyPermissionsRoutes = [
  {
    path: '/auth/empty-permissions',
    loader: () => import('src/view/auth/EmptyPermissionsPage'),
  },
].filter(Boolean);

const emailUnverifiedRoutes = [
  {
    path: '/auth/email-unverified',
    loader: () => import('src/view/auth/EmailUnverifiedPage'),
  },
].filter(Boolean);

const simpleRoutes = [
  {
    path: '/auth/password-reset',
    loader: () => import('src/view/auth/PasswordResetPage'),
  },
  {
    path: '/auth/invitation',
    loader: () => import('src/view/auth/InvitationPage'),
  },
  {
    path: '/auth/tenant/suspended',
    loader: () => import('src/view/auth/TenantSuspendedPage'),
  },
  {
    path: '/auth/verify-email',
    loader: () => import('src/view/auth/VerifyEmailPage'),
  },
  {
    path: '/402',
    loader: () => import('src/view/shared/errors/Error402Page'),
  },
  {
    path: '/403',
    loader: () => import('src/view/shared/errors/Error403Page'),
  },
  {
    path: '/500',
    loader: () => import('src/view/shared/errors/Error500Page'),
  },
  {
    path: '*',
    loader: () => import('src/view/shared/errors/Error404Page'),
  },
].filter(Boolean);

export default {
  privateRoutes,
  publicRoutes,
  emptyTenantRoutes,
  emptyPermissionsRoutes,
  emailUnverifiedRoutes,
  simpleRoutes,
};
